"use client"

/**
 * FAQ on the v3 rungs. Not in the Figma frame — Eugene's call was "take our
 * existing section, then rework it", and the rework is typography: the
 * shared `FaqSection` sets its heading in display-sm regular and its
 * questions in title-md, which is the old ladder. This page's sections all
 * run display-md emphasized for the heading, an emphasized body rung for
 * titles, body-lg for copy and the DS outline pill for a secondary link.
 *
 * Same data and behaviour as the shared section: the nine `home.faq*`
 * questions, one open at a time, the same AnimatePresence reveal. The shared
 * component stays untouched — it renders on the live homepage and every /go
 * page, and a page draft is not the place to restyle it.
 */

import { useState } from "react"
import Link from "next/link"
import { m, AnimatePresence } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { homeKeys as k } from "@/i18n/home-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { buttonVariants } from "@/components/ui/button"
import { ChevronDownIcon } from "@/components/ui/icons"
import { HOMEPAGE_FAQ_KEYS } from "@/app/(frontend)/[country]/[locale]/_components/faq-section"
import { cn } from "@/lib/utils"
import { useHomeV4Copy } from "./home-v4-copy"

type FaqKey = (typeof HOMEPAGE_FAQ_KEYS)[number]

/**
 * `keys` picks and orders the site's questions for another page, without the
 * draft's three; /pricing keeps only the ones about paying (Eugene, 30.09).
 */
export function HomeV4Faq({
  section,
  keys,
}: {
  section: string
  keys?: readonly FaqKey[]
}) {
  const t = useTranslations("home")
  const { FAQ_EXTRA } = useHomeV4Copy()
  const lp = useLocalePath()
  const [open, setOpen] = useState<number | null>(null)
  // The site's nine, then the draft's three (serious finding · what the MRI
  // does not see · where technology and AI are used) as page placeholders,
  // not CMS entries (Eugene, 18.09).
  const entries = keys
    ? keys.map((entry) => ({ q: t(entry.q), a: t(entry.a) }))
    : [
        ...HOMEPAGE_FAQ_KEYS.map((entry) => ({ q: t(entry.q), a: t(entry.a) })),
        ...FAQ_EXTRA,
      ]

  return (
    <section className="py-10 md:pt-[92px] md:pb-[100px]">
      <div
        className={`${section} grid grid-cols-1 gap-10 md:grid-cols-[1fr_2fr] md:gap-12`}
      >
        <div>
          <h2 className="type-display-md-emphasized max-w-[386px] text-neutral-900">
            {t(k.faqHeading)}
          </h2>
          <Link
            href={lp("/faq")}
            className={cn(buttonVariants({ variant: "outline" }), "mt-8 px-8")}
          >
            {t(k.faqLink)}
          </Link>
        </div>

        <div className="divide-y divide-neutral-200 border-t border-neutral-200">
          {entries.map((entry, i) => {
            const isOpen = open === i
            return (
              <div key={entry.q}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center justify-between gap-6 py-5 text-left"
                >
                  <span className="type-body-xl-emphasized text-neutral-900">
                    {entry.q}
                  </span>
                  <ChevronDownIcon
                    className={cn(
                      "shrink-0 text-neutral-400 transition-transform duration-200",
                      isOpen && "rotate-180"
                    )}
                  />
                </button>
                {/* safe-animate-presence: conditional-mount — gated by `isOpen=true` */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <m.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                      role="region"
                    >
                      <p className="type-body-lg max-w-[640px] pb-6 text-neutral-600">
                        {entry.a}
                      </p>
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
